<template>
    <div v-if="isVisible" class="video-widget__modal">
        <div class="video-widget__overlay" @click="closePlayer"></div>

        <div class="video-widget__player" ref="videoContent"
            :class="{ 'video-widget__player-one': stories && stories.length == 1 }">

            <div class="video-widget__stories-indicators" v-if="stories && stories.length > 1">
                <span v-for="(story, index) in stories" :key="`story-${story.id}`" :class="['video-widget__story-indicator', {
                    active: index === currentStoryIndex,
                    viewed: viewedStories.includes(story.id)
                }]" @click="goToStory(index)">
                </span>
            </div>
            <!-- Progress Indicators -->
            <div class="video-widget__indicators">
                <span v-for="(video, index) in currentVideos" :key="`video-${index}`" :class="['video-widget__indicator', {
                    active: index === currentVideoIndex,
                    viewed: viewedVideos.includes(`${currentStory.id}-${index}`)
                }]" @click="goToVideo(index)">
                    <div v-if="index === currentVideoIndex" class="video-widget__progress"
                        :style="{ width: `${player.progress}%` }">
                    </div>
                </span>
            </div>

            <!-- Close Button -->
            <button class="video-widget__close" @click="closePlayer">
                <svg viewBox="0 0 24 24" fill="currentColor">
                    <path
                        d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
            </button>

            <!-- Video Element -->
            <video ref="videoPlayer" class="video-widget__video" :src="getCurrentVideoUrl()"
                :poster="getCurrentVideoPoster()" autoplay playsinline preload="metadata" :muted="player.isMuted"
                :loop="hasOnlyOneVideo" @ended="onVideoEnded" @loadedmetadata="onVideoLoaded"
                @timeupdate="updateProgress" @pause="onVideoPause" @play="onVideoPlay" @click="handleVideoClick"
                @touchstart.prevent="handleVideoTouchStart" @touchend.prevent="handleVideoTouchEnd"
                @mousedown="handleVideoMouseDown" @mouseup="handleVideoMouseUp" />

            <div v-if="isVisible" class="video-widget__arrow-controls">
                <button class="video-widget__arrow-btn prev" @click="prevVideo()" :disabled="!canGoPrev">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="icon icon-tabler icons-tabler-outline icon-tabler-chevron-left">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M15 6l-6 6l6 6" />
                    </svg>
                </button>
                <button class="video-widget__arrow-btn next" @click="nextVideo()" :disabled="!canGoNext">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="icon icon-tabler icons-tabler-outline icon-tabler-chevron-right">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M9 6l6 6l-6 6" />
                    </svg>
                </button>
            </div>

            <!-- Controls -->
            <div class="video-widget__controls">
                <button @click="toggleMute" class="video-widget__control">
                    <svg v-if="player.isMuted" viewBox="0 0 24 24" fill="currentColor">
                        <path
                            d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
                    </svg>
                    <svg v-else viewBox="0 0 24 24" fill="currentColor">
                        <path
                            d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                    </svg>
                </button>

                <button @click="shareVideo" class="video-widget__control">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path
                            d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z" />
                    </svg>
                </button>

                <!-- Playback Rate Controls -->
                <div class="video-widget__playback-controls">
                    <button class="video-widget__control video-widget__control--playback"
                        @click="player.playbackRateShow = !player.playbackRateShow">
                        {{ player.playbackRate }}x
                    </button>
                    <div class="video-widget__playback-controls-wrapper" v-show="player.playbackRateShow">
                        <button @click="setVideoPlaybackRate(0.5)"
                            :class="{ active: player.playbackRate === 0.5 }">0.5x</button>
                        <button @click="setVideoPlaybackRate(1)"
                            :class="{ active: player.playbackRate === 1 }">1x</button>
                        <button @click="setVideoPlaybackRate(1.5)"
                            :class="{ active: player.playbackRate === 1.5 }">1.5x</button>
                        <button @click="setVideoPlaybackRate(2)"
                            :class="{ active: player.playbackRate === 2 }">2x</button>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="keyboardFeedback.showFeedback" class="video-widget__keyboard-feedback">
            {{ keyboardFeedback.lastKey }}
        </div>

        <!-- Loading Overlay -->
        <div v-if="state.isLoading" class="video-widget__loading">
            <div class="video-widget__spinner"></div>
        </div>
    </div>
</template>

<script setup>
import {
    ref,
    reactive,
    computed,
    onMounted,
    onBeforeUnmount,
    nextTick,
    watch
} from 'vue'

const props = defineProps({
    stories: {
        type: Array,
        default: () => []
    },
    initialStoryId: {
        type: [String, Number],
        default: null
    },
    initialVideoIndex: {
        type: Number,
        default: 0
    },
    autoplay: {
        type: Boolean,
        default: true
    },
    muted: {
        type: Boolean,
        default: true
    },
    showControls: {
        type: Boolean,
        default: true
    },
    analytics: {
        type: Object,
        default: null
    },
    isVisible: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits([
    'video-start',
    'video-end',
    'story-change',
    'video-progress',
    'player-open',
    'player-close',
    'video-interaction'
])

// Refs
const videoPlayer = ref(null)
const videoContent = ref(null)

// State
const state = reactive({
    isLoading: false,
    isFullscreen: false,
    error: null
})

const ui = reactive({
    videoContentWidth: 0,
    videoContentHeight: 0
})

const currentStory = ref(null)
const currentVideoIndex = ref(props.initialVideoIndex)
const viewedVideos = ref([])
const viewedStories = ref([])

const player = reactive({
    progress: 0,
    duration: 0,
    currentTime: 0,
    isPlaying: false,
    isMuted: props.muted,
    playbackRate: 1,
    playbackRateShow: false,
    isBuffering: false
})

const interaction = reactive({
    startTime: 0,
    startPos: { x: 0, y: 0 },
    endPos: { x: 0, y: 0 },
    clickThreshold: 200,
    moveThreshold: 15,
    isHolding: false,
    wasPlayingBeforeHold: false,
    isMouseDown: false,
    holdTimeout: null,
    holdThreshold: 300,
    isNavigationClick: false
})

const eventState = reactive({
    lastVideoStartId: null,
    lastStoryChangeId: null
})

const keyboardFeedback = reactive({
    lastKey: null,
    showFeedback: false,
    feedbackTimeout: null
})

let progressInterval = null
let resizeTimeout = null

// Computed
const currentVideos = computed(() => {
    if (!currentStory.value?.videos) return []
    return currentStory.value.videos
})

const hasOnlyOneVideo = computed(() => {
    const isLastStory = currentStoryIndex.value === props.stories.length - 1
    const isLastVideo = currentVideoIndex.value === currentVideos.value.length - 1
    const totalVideosInStory = currentVideos.value.length

    return (isLastStory && isLastVideo) || (totalVideosInStory === 1 && isLastStory)
})

const shouldNavigate = computed(() => {
    return canGoNext.value || canGoPrev.value
})

const canGoNext = computed(() => {
    const hasMoreVideos = currentVideoIndex.value < currentVideos.value.length - 1
    const hasMoreStories = currentStoryIndex.value < props.stories.length - 1
    return hasMoreVideos || hasMoreStories
})

const canGoPrev = computed(() => {
    return currentStoryIndex.value > 0 || currentVideoIndex.value > 0
})

const currentStoryIndex = computed(() => {
    if (!currentStory.value) return -1
    return props.stories.findIndex(s => s.id === currentStory.value.id)
})

const currentVideoId = computed(() => {
    if (!currentStory.value) return null
    return `${currentStory.value.id}-${currentVideoIndex.value}`
})

// watch isVisible to handle body lock and init
watch(() => props.isVisible, (newVal) => {
    if (newVal) {
        initPlayer()
    } else {
        cleanupPlayer()
    }
})

// Methods
const initPlayer = () => {
    state.isLoading = true
    bodyLock()
    addKeyboardListeners()

    if (props.initialStoryId) {
        const story = props.stories.find(s => s.id === props.initialStoryId)
        if (story) {
            setCurrentStory(story)
        }
    } else if (props.stories.length > 0) {
        setCurrentStory(props.stories[0])
    }

    currentVideoIndex.value = props.initialVideoIndex

    nextTick(() => {
        updateVideoContentDimensions()
        state.isLoading = false
    })
}

const cleanupPlayer = () => {
    clearProgressTracking()
    viewedVideos.value = []
    viewedStories.value = []
    resetInteractionState()
    bodyUnlock()
    removeKeyboardListeners()

    if (videoPlayer.value) {
        videoPlayer.value.pause()
        player.isPlaying = false
    }
}

const getVideoUrl = (videoData) => {
    if (!videoData) return ''
    if (typeof videoData === 'string') return videoData
    return videoData.url || videoData.attachment || ''
}

const getVideoPoster = (videoData) => {
    if (!videoData) return ''
    if (typeof videoData === 'object') return videoData.thumbnail || videoData.poster || ''
    return ''
}

const getCurrentVideoUrl = () => {
    if (!currentStory.value?.videos?.[currentVideoIndex.value]) return ''
    return getVideoUrl(currentStory.value.videos[currentVideoIndex.value])
}

const getCurrentVideoPoster = () => {
    if (!currentStory.value?.videos?.[currentVideoIndex.value]) return ''
    return getVideoPoster(currentStory.value.videos[currentVideoIndex.value])
}

const updateVideoContentDimensions = () => {
    if (videoContent.value) {
        const dimensions = calculateVideoHeight()
        videoContent.value.style.width = `${dimensions.width}px`
        videoContent.value.style.height = `${dimensions.height}px`
        videoContent.value.style.maxWidth = 'none'
        videoContent.value.style.maxHeight = 'none'
        videoContent.value.style.minWidth = '300px'
        ui.videoContentWidth = dimensions.width
        ui.videoContentHeight = dimensions.height
    }
}

const calculateVideoHeight = () => {
    const viewportHeight = window.innerHeight
    const viewportWidth = window.innerWidth
    const heightMargin = 0.94
    const widthMargin = 0.94
    const maxHeight = viewportHeight * heightMargin
    const maxWidth = viewportWidth * widthMargin
    const aspectRatio = 9 / 16

    let width = maxHeight * aspectRatio
    let height = maxHeight

    if (width > maxWidth) {
        width = maxWidth
        height = width / aspectRatio
    }
    return { width, height }
}

const handleResize = () => {
    if (resizeTimeout) clearTimeout(resizeTimeout)
    resizeTimeout = setTimeout(() => {
        if (props.isVisible) updateVideoContentDimensions()
    }, 250)
}

const setCurrentStory = (story) => {
    currentStory.value = story
    currentVideoIndex.value = 0
    player.progress = 0
    player.currentTime = 0
    player.duration = 0

    if (props.analytics) {
        props.analytics.trackStoryChange({
            id: story.id,
            name: story.name,
            description: story.description,
            videos: story.videos
        })
    }

    if (eventState.lastStoryChangeId !== story.id) {
        eventState.lastStoryChangeId = story.id
        emit('story-change', story)
    }

    nextTick(() => {
        updateVideoContentDimensions()
        if (videoPlayer.value) videoPlayer.value.load()
    })
}

const nextVideo = () => {
    const fromVideo = currentVideoId.value
    if (currentVideoId.value && !viewedVideos.value.includes(currentVideoId.value)) {
        viewedVideos.value.push(currentVideoId.value)
    }

    if (currentVideoIndex.value < currentVideos.value.length - 1) {
        currentVideoIndex.value++
        resetVideo()
    } else if (currentStoryIndex.value < props.stories.length - 1) {
        if (currentStory.value && !viewedStories.value.includes(currentStory.value.id)) {
            viewedStories.value.push(currentStory.value.id)
        }
        const nextStory = props.stories[currentStoryIndex.value + 1]
        setCurrentStory(nextStory)
    }

    if (props.analytics) {
        props.analytics.trackNavigation('next', fromVideo, currentVideoId.value)
    }
}

const prevVideo = () => {
    const fromVideo = currentVideoId.value
    if (currentVideoIndex.value > 0) {
        currentVideoIndex.value--
        resetVideo()
    } else if (currentStoryIndex.value > 0) {
        const prevStory = props.stories[currentStoryIndex.value - 1]
        setCurrentStory(prevStory)
        currentVideoIndex.value = prevStory.videos.length - 1
        resetVideo()
    }

    if (props.analytics) {
        props.analytics.trackNavigation('prev', fromVideo, currentVideoId.value)
    }
}

const goToStory = (index) => {
    if (index >= 0 && index < props.stories.length && index !== currentStoryIndex.value) {
        setCurrentStory(props.stories[index])
    }
}

const goToVideo = (index) => {
    if (index >= 0 && index < currentVideos.value.length && index !== currentVideoIndex.value) {
        currentVideoIndex.value = index
        resetVideo()
    }
}

const resetVideo = () => {
    clearProgressTracking()
    player.currentTime = 0
    player.progress = 0
    resetInteractionState()
    eventState.lastVideoStartId = null

    nextTick(() => {
        if (videoPlayer.value) {
            videoPlayer.value.currentTime = 0
            videoPlayer.value.load()
        }
    })
}

const togglePlayPause = () => {
    if (videoPlayer.value) {
        if (player.isPlaying) {
            videoPlayer.value.pause()
            if (props.analytics) props.analytics.trackVideoInteraction('pause')
            emit('video-interaction', { type: 'pause', timestamp: Date.now() })
        } else {
            videoPlayer.value.play()
            if (props.analytics) props.analytics.trackVideoInteraction('play')
            emit('video-interaction', { type: 'play', timestamp: Date.now() })
        }
    }
}

const toggleMute = () => {
    if (videoPlayer.value) {
        player.isMuted = !player.isMuted
        videoPlayer.value.muted = player.isMuted
        if (props.analytics) props.analytics.trackVideoInteraction('mute_toggle', { muted: player.isMuted })
        emit('video-interaction', { type: 'mute_toggle', muted: player.isMuted, timestamp: Date.now() })
    }
}

const setVideoPlaybackRate = (rate) => {
    if (videoPlayer.value) {
        videoPlayer.value.playbackRate = rate
        player.playbackRate = rate
        player.playbackRateShow = false
        if (props.analytics) props.analytics.trackVideoInteraction('playback_rate_change', { rate })
        emit('video-interaction', { type: 'playback_rate_change', rate, timestamp: Date.now() })
    }
}

const closePlayer = () => {
    emit('player-close')
}

const calculateDistance = (p1, p2) => Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2))

const resetInteractionState = () => {
    interaction.isHolding = false
    interaction.isMouseDown = false
    if (interaction.holdTimeout) {
        clearTimeout(interaction.holdTimeout)
        interaction.holdTimeout = null
    }
}

const handleVideoMouseDown = (event) => {
    interaction.startTime = Date.now()
    interaction.startPos = { x: event.clientX, y: event.clientY }
    interaction.isMouseDown = true
    if (videoPlayer.value) interaction.wasPlayingBeforeHold = !videoPlayer.value.paused
    interaction.holdTimeout = setTimeout(() => {
        if (interaction.isMouseDown) {
            interaction.isHolding = true
            if (videoPlayer.value) videoPlayer.value.pause()
        }
    }, interaction.holdThreshold)
}

const handleVideoMouseUp = (event) => {
    interaction.endPos = { x: event.clientX, y: event.clientY }
    interaction.isMouseDown = false
    if (interaction.holdTimeout) clearTimeout(interaction.holdTimeout)
    if (interaction.isHolding && interaction.wasPlayingBeforeHold) {
        if (videoPlayer.value) videoPlayer.value.play()
    }
}

const handleVideoClick = (event) => {
    const clickDuration = Date.now() - interaction.startTime
    const distance = calculateDistance(interaction.startPos, { x: event.clientX, y: event.clientY })

    if (clickDuration < interaction.clickThreshold && distance < interaction.moveThreshold && !interaction.isHolding) {
        if (shouldNavigate.value) {
            handleVideoNavigation(event.clientX)
        } else {
            togglePlayPause()
        }
    }
    resetInteractionState()
}

const handleVideoNavigation = (clientX) => {
    const rect = videoContent.value.getBoundingClientRect()
    const relativeX = clientX - rect.left
    if (relativeX < rect.width / 2) {
        if (canGoPrev.value) prevVideo()
    } else {
        if (canGoNext.value) nextVideo()
    }
}

const handleVideoTouchStart = (event) => {
    interaction.startTime = Date.now()
    interaction.startPos = { x: event.touches[0].clientX, y: event.touches[0].clientY }
    if (videoPlayer.value) interaction.wasPlayingBeforeHold = !videoPlayer.value.paused
    interaction.holdTimeout = setTimeout(() => {
        if (interaction.startTime > 0) {
            interaction.isHolding = true
            if (videoPlayer.value) videoPlayer.value.pause()
        }
    }, 300)
}

const handleVideoTouchEnd = (event) => {
    const endPos = { x: event.changedTouches[0].clientX, y: event.changedTouches[0].clientY }
    const distance = calculateDistance(interaction.startPos, endPos)
    if (Date.now() - interaction.startTime < interaction.clickThreshold && distance < interaction.moveThreshold && !interaction.isHolding) {
        handleVideoNavigation(endPos.x)
    }
    if (interaction.wasPlayingBeforeHold && videoPlayer.value) videoPlayer.value.play()
    resetInteractionState()
}

const onVideoLoaded = () => {
    if (videoPlayer.value) {
        player.duration = videoPlayer.value.duration
        startProgressTracking()
    }
}

const onVideoPlay = () => {
    player.isPlaying = true
    startProgressTracking()
    const currentId = currentVideoId.value
    if (eventState.lastVideoStartId !== currentId) {
        eventState.lastVideoStartId = currentId
        const videoData = {
            id: currentId,
            title: currentStory.value?.name,
            storyId: currentStory.value?.id,
            position: currentVideoIndex.value,
            duration: player.duration,
            url: getCurrentVideoUrl()
        }
        if (props.analytics) props.analytics.trackVideoStart(videoData)
        emit('video-start', currentStory.value, videoData)
    }
}

const onVideoPause = () => { player.isPlaying = false }

const onVideoEnded = () => {
    const videoData = { id: currentVideoId.value, completed: true }
    if (props.analytics) props.analytics.trackVideoEnd(videoData)
    emit('video-end', videoData)
    if (canGoNext.value) nextVideo()
    else player.isPlaying = false
}

const updateProgress = () => {
    if (videoPlayer.value && player.duration > 0) {
        player.currentTime = videoPlayer.value.currentTime
        player.progress = (player.currentTime / player.duration) * 100
        const progressData = { progress: player.progress, currentTime: player.currentTime, duration: player.duration, videoId: currentVideoId.value }
        if (props.analytics) props.analytics.trackVideoProgress(progressData)
        emit('video-progress', progressData)
    }
}

const startProgressTracking = () => {
    clearProgressTracking()
    progressInterval = setInterval(updateProgress, 100)
}

const clearProgressTracking = () => {
    if (progressInterval) {
        clearInterval(progressInterval)
        progressInterval = null
    }
}

const bodyLock = () => { document.querySelector('html').style.overflow = 'hidden' }
const bodyUnlock = () => { document.querySelector('html').style.overflow = 'initial' }

const handleKeyDown = (event) => {
    if (!props.isVisible) return
    switch (event.key) {
        case 'Escape': closePlayer(); break
        case 'ArrowLeft': if (canGoPrev.value) prevVideo(); break
        case 'ArrowRight': if (canGoNext.value) nextVideo(); break
        case ' ': togglePlayPause(); event.preventDefault(); break
    }
}

const addKeyboardListeners = () => window.addEventListener('keydown', handleKeyDown)
const removeKeyboardListeners = () => window.removeEventListener('keydown', handleKeyDown)

const shareVideo = async () => {
    const shareData = { title: currentStory.value?.name, url: window.location.href }
    if (navigator.share) await navigator.share(shareData).catch(() => { })
}

// Lifecycle
onMounted(() => {
    window.addEventListener('resize', handleResize)
    if (props.isVisible) initPlayer()
})

onBeforeUnmount(() => {
    window.removeEventListener('resize', handleResize)
    cleanupPlayer()
})

defineExpose({
    open: initPlayer,
    close: cleanupPlayer,
    next: nextVideo,
    prev: prevVideo,
    togglePlayPause,
    toggleMute
})
</script>